/**
 * What the front screens let you hear: the nature ambience of the picture (rain, wind, fire, water, crickets),
 * the lofi music, or both. Remembered in this browser (localStorage, guarded: private mode and tests have none).
 * The front's audio (audio.ts) follows it: ambience fades out in 'bgm', the music fades out in 'nature'.
 */
export type SoundMode = 'nature' | 'bgm' | 'both';
export const SOUND_MODES: readonly SoundMode[] = ['nature', 'bgm', 'both'];
export const SOUND_MODE_KEY = 'c0front.soundMode';

const valid = (v: unknown): v is SoundMode => v === 'nature' || v === 'bgm' || v === 'both';
function load(): SoundMode {
  try {
    const v = localStorage.getItem(SOUND_MODE_KEY);
    if (valid(v)) return v;
  } catch {
    /* no storage */
  }
  return 'both';
}

let mode: SoundMode = load();
const subs = new Set<(m: SoundMode) => void>();

export const getSoundMode = (): SoundMode => mode;
export const wantsMusic = (m: SoundMode = mode): boolean => m !== 'nature';
export const wantsNature = (m: SoundMode = mode): boolean => m !== 'bgm';

export function setSoundMode(m: SoundMode): void {
  if (!valid(m) || m === mode) return;
  mode = m;
  try {
    localStorage.setItem(SOUND_MODE_KEY, m);
  } catch {
    /* no storage */
  }
  for (const f of subs) f(m);
}

/** Calls `f` on each change; returns the unsubscribe function. */
export function subscribeSoundMode(f: (m: SoundMode) => void): () => void {
  subs.add(f);
  return () => subs.delete(f);
}
